import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { InsightsView } from "../../components/insights/InsightsView";
import { RangeSelect } from "../../components/insights/RangeSelect";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const COLS = [["views", "Views"], ["qr_scans", "QR scans"], ["unique_visitors", "Visitors"], ["saves", "Saved"], ["clicks", "Taps"]];

const EmployeeRow = ({ row, onOpen }) => {
  const { t, pick } = useLang();
  return (
  <tr onClick={() => onOpen(row)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50" data-testid={`insights-row-${row.id}`}>
    <td className="px-5 py-3">
      <p className="font-semibold text-slate-900">{pick(row.name_en, row.name_ar)}{!row.is_active && <span className="ms-2 text-xs font-normal text-slate-400">{t("(inactive)")}</span>}</p>
      <p className="text-xs text-slate-500">{pick(row.title_en, row.title_ar)}</p>
    </td>
    {COLS.map(([k]) => <td key={k} className="px-5 py-3 font-mono text-sm text-slate-800" data-testid={`insights-row-${row.id}-${k}`}>{row[k]}</td>)}
  </tr>
  );
};

const EmployeeInsightsDialog = ({ row, days, onClose }) => {
  const { t, pick } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!row) return;
    setData(null);
    api.get(`/admin/insights/${row.id}`, { params: { days } }).then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e)));
  }, [row, days]);
  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto rounded-3xl" data-testid="employee-insights-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-xl">{row && t("{name} · last {days} days", { name: pick(row.name_en, row.name_ar), days })}</DialogTitle></DialogHeader>
        {data ? <InsightsView data={data} /> : <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />}
      </DialogContent>
    </Dialog>
  );
};

export const InsightsPanel = () => {
  const { t } = useLang();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/admin/insights", { params: { days } }).then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e)));
  }, [days]);

  return (
    <div className="space-y-6" data-testid="insights-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">{t("Activity across all cards. Counts are anonymous; employees' visits to their own card are excluded.")}</p>
        <RangeSelect value={days} onChange={setDays} />
      </div>
      {!data ? <div className="h-96 animate-pulse rounded-3xl bg-slate-200" /> : (
        <>
          <InsightsView data={data} />
          <div className="overflow-x-auto rounded-3xl border border-slate-200/80 bg-white" data-testid="insights-employee-table">
            <table className="w-full min-w-[720px] text-start">
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3 text-start">{t("Employee")}</th>
                  {COLS.map(([k, label]) => <th key={k} className="px-5 py-3 text-start">{t(label)}</th>)}
                </tr>
              </thead>
              <tbody>{data.employees.map((r) => <EmployeeRow key={r.id} row={r} onOpen={setOpen} />)}</tbody>
            </table>
          </div>
        </>
      )}
      <EmployeeInsightsDialog row={open} days={days} onClose={() => setOpen(null)} />
    </div>
  );
};
